import React, { useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import axios from "axios";
import ReCAPTCHA from "react-google-recaptcha";
import Swal from "sweetalert2";
import { courses } from "../data/Courses";

const RECAPTCHA_SITE_KEY = window.RECAPTCHA_SITE_KEY || process.env.REACT_APP_API_KEY;

const QUALIFICATIONS = [
  "Grade 12 / NQF Level 4",
  "Certificate (NQF Level 5)",
  "Diploma (NQF Level 6)",
  "Bachelor's Degree (NQF Level 7)",
  "Postgraduate / Honours",
  "Master's or Doctorate",
  "Other",
];

const REFERRAL_SOURCES = [
  "Google Search",
  "Social Media (Facebook / Instagram / LinkedIn)",
  "Word of Mouth",
  "Email / Newsletter",
  "Existing Student",
  "Other",
];

const Apply = () => {
  const { courseName } = useParams();
  const navigate = useNavigate();

  const course = courses.find(
    (c) => c.originalName.replace(".html", "") === courseName
  );
  const courseDisplayName = course?.displayName || courseName;

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    highestQualification: "",
    currentOccupation: "",
    referralSource: "",
    motivation: "",
    acceptTerms: false,
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const [recaptchaError, setRecaptchaError] = useState("");
  const recaptchaRef = useRef(null);

  const validate = () => {
    const e = {};
    if (!formData.fullName.trim())        e.fullName = "Full name is required.";
    if (!formData.email.trim())           e.email = "Email address is required.";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) e.email = "Enter a valid email address.";
    if (!formData.phone.trim())           e.phone = "Phone number is required.";
    if (!formData.highestQualification)   e.highestQualification = "Please select your highest qualification.";
    if (!formData.motivation.trim())      e.motivation = "Please tell us why you want to study this course.";
    if (!formData.acceptTerms)            e.acceptTerms = "You must accept the terms and conditions.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (RECAPTCHA_SITE_KEY && !recaptchaValue) {
      setRecaptchaError("Please complete the reCAPTCHA.");
      return;
    }

    try {
      setSubmitting(true);
      const response = await axios.post(
        "/apply.php",
        { ...formData, course: courseDisplayName, recaptchaToken: recaptchaValue || "" },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.status === 200) {
        Swal.fire({
          title: "Application Submitted!",
          text: "Thank you for applying. Our team will review your application and contact you shortly.",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => navigate("/courses"));

        setFormData({
          fullName: "", email: "", phone: "",
          highestQualification: "", currentOccupation: "",
          referralSource: "", motivation: "", acceptTerms: false,
        });
        if (recaptchaRef.current) recaptchaRef.current.reset();
        setRecaptchaValue(null);
      }
    } catch (err) {
      const serverMessage = err.response?.data?.message;
      if (recaptchaRef.current) recaptchaRef.current.reset();
      setRecaptchaValue(null);
      Swal.fire({
        title: "Submission Failed",
        text: `${serverMessage || "There was a problem submitting your application."} If the problem continues, contact us at info@ikusasatech.com.`,
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (field) =>
    `w-full border rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#016188] transition ${
      errors[field] ? "border-red-400" : "border-gray-300"
    }`;

  return (
    <div className="bg-gray-50 min-h-screen py-12 px-4">
      <Helmet>
        <title>Apply — {courseDisplayName} | Ikusasa Technology Solutions</title>
        <meta
          name="description"
          content={`Apply for the ${courseDisplayName} course offered by Ikusasa Technology Solutions.`}
        />
      </Helmet>

      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-[#016188] text-white rounded-2xl px-8 py-7 mb-8">
          <p className="text-blue-200 text-xs font-bold uppercase tracking-widest mb-1">
            Course Application
          </p>
          <h1 className="text-2xl md:text-3xl font-extrabold leading-tight">
            {courseDisplayName}
          </h1>
          <p className="text-blue-100 text-sm mt-2">
            Complete the form below and our admissions team will be in touch.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 space-y-6">

          {/* Personal details */}
          <div>
            <h2 className="text-base font-extrabold text-gray-700 uppercase tracking-wide mb-4">
              Personal Details
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text" name="fullName" value={formData.fullName}
                  onChange={handleChange} placeholder="e.g. Thabo Mokoena"
                  className={inputClass("fullName")}
                />
                {errors.fullName && <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-600 mb-1">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email" name="email" value={formData.email}
                    onChange={handleChange} placeholder="you@example.com"
                    className={inputClass("email")}
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-600 mb-1">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel" name="phone" value={formData.phone}
                    onChange={handleChange} placeholder="e.g. 071 234 5678"
                    className={inputClass("phone")}
                  />
                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                </div>
              </div>

            </div>
          </div>

          <hr className="border-gray-100" />

          {/* Background */}
          <div>
            <h2 className="text-base font-extrabold text-gray-700 uppercase tracking-wide mb-4">
              Background
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">
                  Highest Qualification <span className="text-red-500">*</span>
                </label>
                <select
                  name="highestQualification" value={formData.highestQualification}
                  onChange={handleChange}
                  className={inputClass("highestQualification")}
                >
                  <option value="">Select qualification</option>
                  {QUALIFICATIONS.map((q) => (
                    <option key={q} value={q}>{q}</option>
                  ))}
                </select>
                {errors.highestQualification && (
                  <p className="text-red-500 text-xs mt-1">{errors.highestQualification}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">
                  Current Occupation
                </label>
                <input
                  type="text" name="currentOccupation" value={formData.currentOccupation}
                  onChange={handleChange} placeholder="e.g. IT Support Technician"
                  className={inputClass("currentOccupation")}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">
                  How did you hear about us?
                </label>
                <select
                  name="referralSource" value={formData.referralSource}
                  onChange={handleChange}
                  className={inputClass("referralSource")}
                >
                  <option value="">Select an option</option>
                  {REFERRAL_SOURCES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <hr className="border-gray-100" />

          {/* Motivation */}
          <div>
            <h2 className="text-base font-extrabold text-gray-700 uppercase tracking-wide mb-4">
              Motivation
            </h2>
            <div>
              <label className="block text-sm font-semibold text-gray-600 mb-1">
                Why do you want to study this course? <span className="text-red-500">*</span>
              </label>
              <textarea
                name="motivation" value={formData.motivation}
                onChange={handleChange} rows={5}
                placeholder="Tell us about your goals and what you hope to achieve..."
                className={inputClass("motivation")}
              />
              {errors.motivation && <p className="text-red-500 text-xs mt-1">{errors.motivation}</p>}
            </div>
          </div>

          {/* Terms */}
          <div>
            <label className="flex items-start gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                name="acceptTerms"
                checked={formData.acceptTerms}
                onChange={handleChange}
                className="mt-1"
              />
              <span>
                I have read and accept the{" "}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#016188] font-semibold hover:underline"
                >
                  terms and conditions
                </a>
                , including the fees, cancellation and refund policy. <span className="text-red-500">*</span>
              </span>
            </label>
            {errors.acceptTerms && <p className="text-red-500 text-xs mt-1">{errors.acceptTerms}</p>}
          </div>

          {/* reCAPTCHA */}
          {RECAPTCHA_SITE_KEY && (
            <div className="flex flex-col items-start gap-1">
              <ReCAPTCHA
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={(val) => { setRecaptchaValue(val); setRecaptchaError(""); }}
                ref={recaptchaRef}
              />
              {recaptchaError && <p className="text-red-500 text-xs">{recaptchaError}</p>}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#930a0d] hover:bg-red-800 text-white font-bold py-3 rounded-lg transition duration-300 disabled:opacity-60"
          >
            {submitting ? "Submitting…" : "Submit Application"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Apply;
